import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { Plus, Edit2, Trash2, X, AlertCircle, CheckCircle2, Loader2, Search, FileText} from 'lucide-react';

const FeeStructures = () => {
  const { api, user } = useAuth();

  // State management
  const [feeStructures, setFeeStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedFee, setSelectedFee] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Form states
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [formErrors, setFormErrors] = useState({});

  // Fetch fee structures
  const fetchFeeStructures = async () => {
    try {
      setLoading(true);
      showNotification('info', 'Loading fee structures...');
      
      const response = await api.get(`/api/v1/fee-structures/school/${user.school_id}`);
      setFeeStructures(response.data);
      showNotification('success', 'Fee structures loaded successfully');
    } catch (error) {
      console.error('Error fetching fee structures:', error);
      showNotification('error', 'Failed to load fee structures');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeeStructures();
  }, []);

  // Notification system
  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: '', message: '' });
    }, 4000);
  };

  // Handle form validation
  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle API errors
  const handleApiError = (error) => {
    if (error.response?.status === 422) {
      const errorData = error.response.data;
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          showNotification('error', errorData.detail);
        } else if (Array.isArray(errorData.detail)) {
          const errorMessages = errorData.detail.map(err => err.msg).join(', ');
          showNotification('error', errorMessages);
        }
      }
    } else {
      showNotification('error', 'An unexpected error occurred');
    }
  };

  // Create fee structure
  const handleCreate = async () => {
    if (!validateForm()) return;

    try {
      setModalLoading(true);
      showNotification('info', 'Creating fee structure...');
      
      const response = await api.post(
        `/api/v1/fee-structures/school/${user.school_id}/create`,
        formData
      );
      
      setFeeStructures(prev => [...prev, response.data]);
      setShowCreateModal(false);
      setFormData({ name: '', description: '' });
      setFormErrors({});
      showNotification('success', 'Fee structure created successfully');
    } catch (error) {
      handleApiError(error);
    } finally {
      setModalLoading(false);
    }
  };

  // Update fee structure
  const handleUpdate = async () => {
    if (!validateForm()) return;

    try {
      setModalLoading(true);
      showNotification('info', 'Updating fee structure...');
      
      const response = await api.put(
        `/api/v1/fee-structures/${selectedFee.id}/school/${user.school_id}/update`,
        formData
      );
      
      setFeeStructures(prev => 
        prev.map(fee => fee.id === selectedFee.id ? response.data : fee)
      );
      setShowUpdateModal(false);
      setSelectedFee(null);
      setFormData({ name: '', description: '' });
      setFormErrors({});
      showNotification('success', 'Fee structure updated successfully');
    } catch (error) {
      handleApiError(error);
    } finally {
      setModalLoading(false);
    }
  };

  // Delete fee structure
  const handleDelete = async () => {
    try {
      setModalLoading(true);
      showNotification('info', 'Deleting fee structure...');
      
      await api.delete(
        `/api/v1/fee-structures/${selectedFee.id}/school/${user.school_id}/delete`
      );
      
      setFeeStructures(prev => prev.filter(fee => fee.id !== selectedFee.id));
      setShowDeleteModal(false);
      setSelectedFee(null);
      showNotification('success', 'Fee structure deleted successfully');
    } catch (error) {
      handleApiError(error);
    } finally {
      setModalLoading(false);
    }
  };

  // Open modals
  const openCreateModal = () => {
    setFormData({ name: '', description: '' });
    setFormErrors({});
    setShowCreateModal(true);
  };

  const openUpdateModal = (fee) => {
    setSelectedFee(fee);
    setFormData({ name: fee.name, description: fee.description });
    setFormErrors({});
    setShowUpdateModal(true);
  };

  const openDeleteModal = (fee) => {
    setSelectedFee(fee);
    setShowDeleteModal(true);
  };

  // Navigate to fee details
  const handleRowClick = (feeId) => {
    window.location.href = `/fees-structure/${feeId}`;
  };

  // Filter fee structures
  const filteredFeeStructures = feeStructures.filter(fee =>
    fee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    fee.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Notification Bar */}
        {notification.show && (
          <div className={`mb-6 p-4 rounded-lg flex items-center space-x-3 transition-all duration-300 ${
            notification.type === 'success' ? 'bg-green-50 border border-green-200' :
            notification.type === 'error' ? 'bg-red-50 border border-red-200' :
            'bg-blue-50 border border-blue-200'
          }`}>
            {notification.type === 'success' && <CheckCircle2 className="text-green-600 w-5 h-5" />}
            {notification.type === 'error' && <AlertCircle className="text-red-600 w-5 h-5" />}
            {notification.type === 'info' && <Loader2 className="text-blue-600 w-5 h-5 animate-spin" />}
            <span className={`text-sm font-medium ${
              notification.type === 'success' ? 'text-green-800' :
              notification.type === 'error' ? 'text-red-800' :
              'text-blue-800'
            }`}>
              {notification.message}
            </span>
          </div>
        )}

        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Fee Management</h1>
              <p className="text-gray-600 mt-1">Manage your school's fee structures</p>
            </div>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center px-4 py-2 bg-[#0E5252] text-white rounded-lg hover:bg-[#0a4242] transition-colors duration-200 shadow-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Fee Structure
            </button>
          </div>

          {/* Search Bar */}
          <div className="mt-6 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search fee structures..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#0E5252]" />
              <span className="ml-3 text-gray-600">Loading fee structures...</span>
            </div>
          ) : filteredFeeStructures.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No fee structures found</h3>
              <p className="text-gray-600 text-center max-w-md">
                {searchTerm ? 'No fee structures match your search criteria.' : 'Get started by creating your first fee structure.'}
              </p>
              {!searchTerm && (
                <button
                  onClick={openCreateModal}
                  className="mt-4 inline-flex items-center px-4 py-2 bg-[#0E5252] text-white rounded-lg hover:bg-[#0a4242] transition-colors duration-200"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Fee Structure
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fee Structure
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredFeeStructures.map((fee) => (
                    <tr 
                      key={fee.id} 
                      onClick={() => handleRowClick(fee.id)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{fee.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600 max-w-xs truncate">{fee.description}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openUpdateModal(fee);
                            }}
                            className="text-[#0E5252] hover:text-[#0a4242] p-1 rounded transition-colors duration-200"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteModal(fee);
                            }}
                            className="text-red-600 hover:text-red-700 p-1 rounded transition-colors duration-200"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Create Fee Structure</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      formErrors.name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter fee structure name"
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      formErrors.description ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter fee structure description"
                  />
                  {formErrors.description && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
                  )}
                </div>
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreate}
                    disabled={modalLoading}
                    className="flex-1 px-4 py-2 bg-[#0E5252] text-white rounded-lg hover:bg-[#0a4242] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {modalLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Create'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Update Modal */}
        {showUpdateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Update Fee Structure</h3>
                <button
                  onClick={() => setShowUpdateModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      formErrors.name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter fee structure name"
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      formErrors.description ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter fee structure description"
                  />
                  {formErrors.description && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
                  )}
                </div>
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => setShowUpdateModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdate}
                    disabled={modalLoading}
                    className="flex-1 px-4 py-2 bg-[#0E5252] text-white rounded-lg hover:bg-[#0a4242] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {modalLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Update'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Confirm Deletion</h3>
                    <p className="text-sm text-gray-600 mt-1">This action cannot be undone.</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-6">
                  Are you sure you want to delete "<span className="font-medium">{selectedFee?.name}</span>"?
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={modalLoading}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {modalLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Delete'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeeStructures;